import { Store } from "../state/store";

/* ---------------- DOM-Helfer ---------------- */

type Child = Node | string | number | null | undefined | false;

interface Props {
  class?: string;
  id?: string;
  html?: string;
  text?: string;
  type?: string;
  value?: string | number;
  placeholder?: string;
  href?: string;
  src?: string;
  alt?: string;
  for?: string;
  name?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  inputmode?: string;
  rows?: number;
  maxlength?: number;
  checked?: boolean;
  disabled?: boolean;
  attrs?: Record<string, string>;
  dataset?: Record<string, string>;
  style?: Partial<CSSStyleDeclaration>;
  on?: Record<string, (e: any) => void>;
}

export function h(tag: string, props: Props = {}, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag);
  if (props.class) el.className = props.class;
  if (props.id) el.id = props.id;
  if (props.html != null) el.innerHTML = props.html;
  if (props.text != null) el.textContent = props.text;
  if (props.type) (el as HTMLInputElement).type = props.type;
  if (props.value != null) (el as HTMLInputElement).value = String(props.value);
  if (props.placeholder) (el as HTMLInputElement).placeholder = props.placeholder;
  if (props.href) (el as HTMLAnchorElement).href = props.href;
  if (props.src) (el as HTMLImageElement).src = props.src;
  if (props.alt) (el as HTMLImageElement).alt = props.alt;
  if (props.for) (el as HTMLLabelElement).htmlFor = props.for;
  if (props.name) (el as HTMLInputElement).name = props.name;
  if (props.min != null) el.setAttribute("min", String(props.min));
  if (props.max != null) el.setAttribute("max", String(props.max));
  if (props.step != null) el.setAttribute("step", String(props.step));
  if (props.inputmode) el.setAttribute("inputmode", props.inputmode);
  if (props.rows != null) (el as HTMLTextAreaElement).rows = props.rows;
  if (props.maxlength != null) el.setAttribute("maxlength", String(props.maxlength));
  if (props.checked) (el as HTMLInputElement).checked = true;
  if (props.disabled) (el as HTMLInputElement).disabled = true;
  if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) el.setAttribute(k, v);
  if (props.dataset) for (const [k, v] of Object.entries(props.dataset)) el.dataset[k] = v;
  if (props.style) Object.assign(el.style, props.style);
  if (props.on) for (const [ev, fn] of Object.entries(props.on)) el.addEventListener(ev, fn);
  for (const c of children) appendChild(el, c);
  return el;
}

function appendChild(parent: HTMLElement, c: Child) {
  if (c == null || c === false) return;
  parent.appendChild(typeof c === "string" || typeof c === "number" ? document.createTextNode(String(c)) : c);
}

export function clear(el: HTMLElement) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/* ---------------- Feld-Wrapper ---------------- */

const OPTIONAL_HINT = "Optional – nur ausfüllen, wenn dir die Angabe bekannt ist.";

interface FieldOpts {
  required?: boolean;
  optional?: boolean;
  optionalText?: string;
  hint?: string;
}

export function field(label: string, control: HTMLElement, opts: FieldOpts = {}): HTMLElement {
  const lbl = h("label", { class: "field__label" }, label);
  if (opts.required) lbl.appendChild(h("span", { class: "field__req", text: "*" }));
  if (control.id) (lbl as HTMLLabelElement).htmlFor = control.id;
  const wrap = h("div", { class: "field" }, lbl, control);
  if (opts.optional) wrap.appendChild(h("span", { class: "field__optional", text: opts.optionalText ?? OPTIONAL_HINT }));
  if (opts.hint) wrap.appendChild(h("span", { class: "field__optional", text: opts.hint }));
  return wrap;
}

let idSeq = 0;
const uid = (p = "f") => `${p}-${++idSeq}`;

/* ---------------- Gebundene Eingaben ---------------- */

interface InputOpts extends FieldOpts {
  label: string;
  type?: string;
  placeholder?: string;
  inputmode?: string;
  onInput?: () => void;
}

export function textInput(store: Store, path: string, opts: InputOpts): HTMLElement {
  const id = uid();
  const input = h("input", {
    id,
    type: opts.type ?? "text",
    value: store.get(path) ?? "",
    placeholder: opts.placeholder,
    inputmode: opts.inputmode,
    on: {
      input: (e: Event) => {
        store.set(path, (e.target as HTMLInputElement).value);
        opts.onInput?.();
      },
    },
  });
  return field(opts.label, input, opts);
}

export function moneyInput(store: Store, path: string, opts: InputOpts): HTMLElement {
  const id = uid();
  const input = h("input", {
    id,
    type: "text",
    inputmode: "decimal",
    value: store.get(path) ?? "",
    placeholder: opts.placeholder ?? "0",
    on: {
      input: (e: Event) => {
        store.set(path, (e.target as HTMLInputElement).value);
        opts.onInput?.();
      },
    },
  });
  const wrap = h("div", { class: "input-prefix" }, h("span", { text: "€" }), input);
  return field(opts.label, wrap, opts);
}

export function textareaInput(store: Store, path: string, opts: InputOpts & { rows?: number; maxlength?: number }): HTMLElement {
  const id = uid();
  const ta = h("textarea", {
    id,
    rows: opts.rows ?? 4,
    maxlength: opts.maxlength,
    placeholder: opts.placeholder,
    value: store.get(path) ?? "",
    on: { input: (e: Event) => { store.set(path, (e.target as HTMLTextAreaElement).value); opts.onInput?.(); } },
  });
  return field(opts.label, ta, opts);
}

export function selectInput(store: Store, path: string, options: string[], opts: InputOpts): HTMLElement {
  const id = uid();
  const cur = store.get(path) ?? "";
  const sel = h("select", {
    id,
    on: { change: (e: Event) => { store.set(path, (e.target as HTMLSelectElement).value); opts.onInput?.(); } },
  });
  sel.appendChild(h("option", { value: "", text: opts.placeholder ?? "Bitte wählen …" }));
  for (const o of options) {
    const op = h("option", { value: o, text: o }) as HTMLOptionElement;
    if (o === cur) op.selected = true;
    sel.appendChild(op);
  }
  return field(opts.label, sel, opts);
}

/* ---------------- Chips (Mehrfachauswahl, Array-Pfad) ---------------- */

export function chipGroup(store: Store, path: string, options: string[], onChange?: () => void): HTMLElement {
  const wrap = h("div", { class: "chips", attrs: { role: "group" } });
  const current: string[] = store.get(path) ?? [];
  for (const o of options) {
    const active = current.includes(o);
    const chip = h("button", {
      type: "button",
      class: "chip",
      text: o,
      attrs: { "aria-pressed": String(active) },
      on: {
        click: () => {
          const arr: string[] = store.get(path) ?? [];
          const idx = arr.indexOf(o);
          if (idx >= 0) arr.splice(idx, 1);
          else arr.push(o);
          store.set(path, arr);
          chip.setAttribute("aria-pressed", String(arr.includes(o)));
          onChange?.();
        },
      },
    });
    wrap.appendChild(chip);
  }
  return wrap;
}

/* ---------------- Auswahl-Karten (Einfachauswahl) ---------------- */

interface OptCard { value: string; title: string; desc?: string }

export function optionCards(
  store: Store,
  path: string,
  options: OptCard[],
  onChange?: () => void,
  row = false
): HTMLElement {
  const wrap = h("div", { class: row ? "options options--row" : "options", attrs: { role: "radiogroup" } });
  const setActive = () => {
    const cur = store.get(path);
    wrap.querySelectorAll<HTMLElement>(".opt").forEach((el) => {
      el.setAttribute("aria-pressed", String(el.dataset.value === cur));
    });
  };
  for (const o of options) {
    const card = h(
      "button",
      {
        type: "button",
        class: "opt",
        dataset: { value: o.value },
        attrs: { "aria-pressed": String(store.get(path) === o.value) },
        on: {
          click: () => {
            store.set(path, o.value);
            setActive();
            onChange?.();
          },
        },
      },
      h("span", { class: "opt__title", text: o.title }),
      o.desc ? h("span", { class: "opt__desc", text: o.desc }) : null
    );
    wrap.appendChild(card);
  }
  return wrap;
}

/** Ja / Nein bzw. Ja / Nein / Weiß-nicht. */
export function jaNein(store: Store, path: string, onChange?: () => void, withWeiss = false): HTMLElement {
  const opts: OptCard[] = [
    { value: "ja", title: "Ja" },
    { value: "nein", title: "Nein" },
  ];
  if (withWeiss) opts.push({ value: "weiss_nicht", title: "Weiß ich nicht" });
  return optionCards(store, path, opts, onChange, true);
}

/* ---------------- Slider 1–10 ---------------- */

export function slider(
  store: Store,
  path: string,
  ends: { left: string; mid: string; right: string },
  explain: (v: number) => string
): HTMLElement {
  const valBadge = h("div", { class: "slider-val", text: String(store.get(path) ?? 5) });
  const explainBox = h("div", { class: "slider-explain", text: explain(store.get(path) ?? 5) });
  const setFill = (input: HTMLInputElement) => {
    const v = Number(input.value);
    input.style.setProperty("--fill", `${((v - 1) / 9) * 100}%`);
    valBadge.textContent = String(v);
    explainBox.textContent = explain(v);
  };
  const range = h("input", {
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    value: store.get(path) ?? 5,
    attrs: { "aria-label": ends.mid },
    on: {
      input: (e: Event) => {
        const input = e.target as HTMLInputElement;
        store.set(path, Number(input.value));
        setFill(input);
      },
    },
  }) as HTMLInputElement;
  // initial fill
  queueMicrotask(() => setFill(range));
  const wrap = h(
    "div",
    { class: "slider-wrap" },
    h("div", { class: "slider-row" }, valBadge, range),
    h(
      "div",
      { class: "slider-ends" },
      h("span", { text: ends.left }),
      h("span", { text: ends.right })
    ),
    explainBox
  );
  return wrap;
}

/* ---------------- Diverse Bausteine ---------------- */

export function note(kind: "info" | "warn" | "dark" | "info-dark" | "warn-dark", text: string): HTMLElement {
  return h("div", { class: `note note--${kind}`, text });
}

export function errorBox(errors: string[]): HTMLElement | null {
  if (errors.length === 0) return null;
  const ul = h("ul");
  errors.forEach((e) => ul.appendChild(h("li", { text: e })));
  return h("div", { class: "errors", attrs: { role: "alert" } },
    h("strong", { text: "Bitte ergänze noch:" }), ul);
}

export function card(...children: Child[]): HTMLElement {
  return h("div", { class: "card" }, ...children);
}

/* ---------------- Objektgebundene Felder (für wiederholbare Karten) ---------------- */

interface ObjOpts extends FieldOpts {
  label: string;
  placeholder?: string;
  money?: boolean;
  disabled?: boolean;
}

export function objText<T extends Record<string, any>>(
  obj: T, key: keyof T, persist: () => void, opts: ObjOpts
): HTMLElement {
  const id = uid();
  const input = h("input", {
    id,
    type: "text",
    inputmode: opts.money ? "decimal" : undefined,
    value: (obj[key] as any) ?? "",
    placeholder: opts.placeholder,
    disabled: opts.disabled,
    on: { input: (e: Event) => { (obj as any)[key] = (e.target as HTMLInputElement).value; persist(); } },
  });
  if (opts.money) {
    const wrap = h("div", { class: "input-prefix" }, h("span", { text: "€" }), input);
    return field(opts.label, wrap, opts);
  }
  return field(opts.label, input, opts);
}

export function objSelect<T extends Record<string, any>>(
  obj: T, key: keyof T, options: string[], persist: () => void, opts: ObjOpts
): HTMLElement {
  const id = uid();
  const sel = h("select", { id, on: { change: (e: Event) => { (obj as any)[key] = (e.target as HTMLSelectElement).value; persist(); } } });
  sel.appendChild(h("option", { value: "", text: opts.placeholder ?? "Bitte wählen …" }));
  for (const o of options) {
    const op = h("option", { value: o, text: o }) as HTMLOptionElement;
    if (o === (obj[key] as any)) op.selected = true;
    sel.appendChild(op);
  }
  return field(opts.label, sel, opts);
}

export function objTextarea<T extends Record<string, any>>(
  obj: T, key: keyof T, persist: () => void, opts: ObjOpts & { rows?: number; maxlength?: number }
): HTMLElement {
  const id = uid();
  const ta = h("textarea", {
    id, rows: opts.rows ?? 3, maxlength: opts.maxlength, placeholder: opts.placeholder,
    value: (obj[key] as any) ?? "",
    on: { input: (e: Event) => { (obj as any)[key] = (e.target as HTMLTextAreaElement).value; persist(); } },
  });
  return field(opts.label, ta, opts);
}

/** "Weiß ich nicht"-Schalter, der ein zugehöriges Feld deaktiviert. */
export function unknownToggle<T extends Record<string, any>>(
  obj: T, key: keyof T, persist: () => void, onChange: () => void, label = "Weiß ich nicht"
): HTMLElement {
  const id = uid("u");
  const input = h("input", {
    id, type: "checkbox", checked: Boolean(obj[key]),
    on: { change: (e: Event) => { (obj as any)[key] = (e.target as HTMLInputElement).checked; persist(); onChange(); } },
  });
  return h("div", { class: "consent", style: { padding: "4px 0" } }, input, h("label", { for: id, text: label }));
}

export function checkbox(store: Store, path: string, label: string | HTMLElement, healthStyle = false, onChange?: () => void): HTMLElement {
  const id = uid("c");
  const input = h("input", {
    id,
    type: "checkbox",
    checked: Boolean(store.get(path)),
    on: { change: (e: Event) => { store.set(path, (e.target as HTMLInputElement).checked); onChange?.(); } },
  });
  const lbl = h("label", { for: id });
  if (typeof label === "string") lbl.textContent = label;
  else lbl.appendChild(label);
  return h("div", { class: `consent${healthStyle ? " consent--health" : ""}` }, input, lbl);
}
